// Thread Message types - Single source of truth matching backend exactly
// Backend reference: app/api/routes/signals.py lines 42-52

export interface ThreadMessage {
  id?: string;
  external_id?: string; // External system's message ID for idempotency
  author_type: 'customer' | 'agent' | 'system' | 'unknown'; // MUST match backend enum
  author_name: string;
  author_email?: string;
  author_avatar_url?: string;
  content: string;
  sent_at: string; // ISO format timestamp string (backend expects datetime)
  message_type: 'customer_message' | 'agent_response' | 'internal_note' | 'unknown_sender'; // MUST match backend enum
  attachments?: Array<{
    filename: string;
    url: string;
    type: string;
  }>;
}

// Legacy format that might come from old data or external systems
export interface LegacyThreadMessage {
  role?: 'customer' | 'agent' | 'system';
  message?: string;
  text?: string;
  timestamp?: string;
  created_at?: string;
}

// Utility function to normalize any message format to ThreadMessage
export function normalizeThreadMessage(
  msg: ThreadMessage | LegacyThreadMessage | unknown,
  fallbackCustomerName: string = 'Customer'
): ThreadMessage {
  const raw = (
    msg && typeof msg === 'object' ? msg : {}
  ) as Partial<ThreadMessage & LegacyThreadMessage> & Record<string, unknown>;

  // If already in correct format, validate and return
  if (raw.author_type && raw.content && raw.sent_at) {
    return {
      id: raw.id,
      external_id: raw.external_id,
      author_type: raw.author_type,
      author_name: raw.author_name || (raw.author_type === 'customer' ? fallbackCustomerName : 'Support Agent'),
      author_email: raw.author_email,
      author_avatar_url: raw.author_avatar_url,
      content: raw.content,
      sent_at: raw.sent_at,
      message_type: raw.message_type || (raw.author_type === 'customer' ? 'customer_message' : 'agent_response'),
      attachments: raw.attachments || []
    };
  }

  // Handle legacy format with 'role' field
  if (raw.role) {
    return {
      author_type: raw.role === 'customer' ? 'customer' : raw.role === 'agent' ? 'agent' : 'system',
      author_name: raw.author_name || (raw.role === 'customer' ? fallbackCustomerName : 'Support Agent'),
      content: raw.content || raw.message || raw.text || '',
      sent_at: raw.sent_at || raw.timestamp || raw.created_at || new Date().toISOString(),
      message_type: raw.message_type || (raw.role === 'customer' ? 'customer_message' : 'agent_response'),
      attachments: raw.attachments || []
    };
  }

  // Fallback for unknown format
  console.warn('Unknown thread message format, using defaults:', msg);
  return {
    author_type: 'customer',
    author_name: fallbackCustomerName,
    content: String(raw.content || raw.message || raw.text || JSON.stringify(msg)),
    sent_at: String(raw.sent_at || raw.timestamp || new Date().toISOString()),
    message_type: 'customer_message',
    attachments: []
  };
}

// Utility to check if a message is from an agent
export function isAgentMessage(msg: ThreadMessage): boolean {
  return msg.author_type === 'agent' || msg.message_type === 'agent_response';
}

// Utility to check if a message is from a customer
export function isCustomerMessage(msg: ThreadMessage): boolean {
  return msg.author_type === 'customer' || msg.message_type === 'customer_message';
}
